import { ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import React from 'react';

import { Button } from '@/components/ui/button';
import { useTradingSessionStore } from '@/features/tradingSession/store';
import type { SpeedJobResponse, SpeedJobStatus } from '@/lib/api/speed';
import { cn } from '@/lib/utils';

import { isActiveSpeedJob } from '../jobStatus';

interface CustodialOperationsPanelProps {
    jobs: SpeedJobResponse[];
    isLoading: boolean;
    expanded: boolean;
    onExpandedChange: (expanded: boolean) => void;
}

const KIND_LABEL: Record<SpeedJobResponse['kind'], string> = {
    deposit: 'Deposit',
    withdraw: 'Withdraw',
    agentSetup: 'Perps wallet setup',
    migrate: 'Migration to V2',
};

const STATUS_LABEL: Record<SpeedJobStatus, string> = {
    queued: 'Queued',
    running: 'Running',
    actionRequired: 'Signature required',
    authorizationRequired: 'Trading authorization required',
    bridging: 'Bridging',
    settled: 'Complete',
    failed: 'Failed',
    refunded: 'Refunded',
};

function withEllipsis(label: string): string {
    const trimmed = label.trim();
    return trimmed.endsWith('...')
        ? trimmed
        : `${trimmed.replace(/\.+$/, '')}...`;
}

function displayMessage(job: SpeedJobResponse): string {
    const label = job.message || STATUS_LABEL[job.status];
    return isActiveSpeedJob(job) ? withEllipsis(label) : label;
}

function formatUpdatedAt(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    return date.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
    });
}

const CustodialOperationsPanel: React.FC<CustodialOperationsPanelProps> = ({
    jobs,
    isLoading,
    expanded,
    onExpandedChange,
}) => {
    const activeCount = jobs.filter(isActiveSpeedJob).length;
    const requestJobReauthorization = useTradingSessionStore(
        (state) => state.requestJobReauthorization,
    );

    if (!isLoading && jobs.length === 0) {
        return null;
    }

    return (
        <div className="border-b border-border bg-background/30">
            <div className="flex items-center justify-between gap-3 px-4 py-2.5">
                <button
                    type="button"
                    onClick={() => onExpandedChange(!expanded)}
                    aria-expanded={expanded}
                    className="flex min-w-0 flex-1 items-center justify-between gap-3 text-left"
                >
                    <div className="min-w-0">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                            Operations
                        </p>
                        {activeCount > 0 && (
                            <p className="mt-0.5 text-[10px] text-info">
                                {activeCount} running...
                            </p>
                        )}
                    </div>
                    {expanded ? (
                        <ChevronUp
                            size={14}
                            className="shrink-0 text-muted-foreground"
                        />
                    ) : (
                        <ChevronDown
                            size={14}
                            className="shrink-0 text-muted-foreground"
                        />
                    )}
                </button>
            </div>

            {expanded && (
                <div className="space-y-2 px-4 pb-3">
                    {isLoading && jobs.length === 0 ? (
                        <div className="flex items-center gap-2 rounded-lg border border-border bg-card/60 px-3 py-2">
                            <Loader2
                                size={13}
                                className="animate-spin text-muted-foreground"
                            />
                            <span className="text-[10px] text-muted-foreground">
                                Loading operations...
                            </span>
                        </div>
                    ) : (
                        jobs.map((job) => {
                            const active = isActiveSpeedJob(job);
                            const progress = Math.min(
                                Math.max(job.progress, 0),
                                100,
                            );
                            return (
                                <div
                                    key={job.jobId}
                                    className={cn(
                                        'rounded-lg border px-3 py-2',
                                        active
                                            ? 'border-border bg-secondary/40'
                                            : 'border-border bg-card/50',
                                    )}
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-foreground">
                                                {KIND_LABEL[job.kind]}
                                            </p>
                                            <p className="mt-1 truncate text-[10px] text-muted-foreground">
                                                {displayMessage(job)}
                                            </p>
                                        </div>
                                        <div className="shrink-0 text-right">
                                            <p
                                                className={cn(
                                                    'text-[10px]',
                                                    active
                                                        ? 'text-info'
                                                        : job.status ===
                                                                'failed' ||
                                                            job.status ===
                                                                'refunded'
                                                            ? 'text-destructive'
                                                            : 'text-muted-foreground',
                                                )}
                                            >
                                                {active
                                                    ? withEllipsis(
                                                        STATUS_LABEL[
                                                            job.status
                                                        ],
                                                    )
                                                    : STATUS_LABEL[job.status]}
                                            </p>
                                            <p className="mt-0.5 font-mono text-[9px] tabular-nums text-muted-foreground">
                                                {formatUpdatedAt(job.updatedAt)}
                                            </p>
                                        </div>
                                    </div>

                                    {(active || progress < 100) && (
                                        <div
                                            role="progressbar"
                                            aria-valuemin={0}
                                            aria-valuemax={100}
                                            aria-valuenow={Math.round(progress)}
                                            aria-label={`${STATUS_LABEL[job.status]} progress`}
                                            className="mt-2 h-1 overflow-hidden rounded-full bg-border"
                                        >
                                            <div
                                                className={cn(
                                                    'h-full rounded-full transition-[width] duration-500',
                                                    active
                                                        ? 'bg-primary'
                                                        : 'bg-muted-foreground/70',
                                                )}
                                                style={{
                                                    width: `${progress}%`,
                                                }}
                                            />
                                        </div>
                                    )}

                                    {job.status === 'authorizationRequired' && (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            className="mt-2 w-full"
                                            onClick={() =>
                                                requestJobReauthorization(
                                                    job.jobId,
                                                )
                                            }
                                        >
                                            Reauthorize and resume
                                        </Button>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
            )}
        </div>
    );
};

export default CustodialOperationsPanel;
